import { StepEditorProps } from "../useStepEditor";
import { StepEditorAccordion } from "../../../workflows/routes/WorkflowEditor/StepEditorAccordion";
import { Block } from "../../../../components/atoms";
import { useStepOptions } from "../useStepOptions";
import { prefixForm } from "../../../../utils/FormUtils";
import { useDataSources } from "../../../datasources/hooks/useDataSources";
import { useMemo } from "react";
import { Option, Select } from "@introist/react-foundation/v2";
import { TagSelector } from "../../../system/tags/TagSelector";
import { TagAwareInput } from "../../components/TagAwareInput";
import { DataSourceIcon } from "../../../datasources/components/DataSourceIcon";

export const HarvestUpdateTimeEntryStepEditor = ({
  form,
  stepDataPath,
  workflowId
}: StepEditorProps) => {
  const prefixedForm = prefixForm(form, stepDataPath);

  const { dataSources } = useDataSources();

  const { options } = useStepOptions(form.data.stepType, {
    datasourceId: prefixedForm.get("datasourceId")
  });

  const harvestSourceOptions = useMemo(() => {
    if (!dataSources) return [];
    return dataSources
      .filter(ds => ds.sourceType === "harvest")
      .map(s => ({
        key: s.id,
        title: s.name,
        startAdornment: <DataSourceIcon dataSource="harvest" size="small" />
      }));
  }, [dataSources]);

  const projectOptions: Option[] = useMemo(() => {
    if (!options || !options.projects) return [];
    return options.projects.map((p: any) => ({ key: p.id, title: p.name }));
  }, [options]);

  const taskOptions = useMemo(() => {
    if (!options || !options.projects) return [];
    const selectedProjectId = prefixedForm.get("projectId");
    const selectedProject = options.projects.find((p: any) => p.id === selectedProjectId);
    if (!selectedProject) return [];

    return selectedProject.tasks.map((p: any) => ({ key: p.id, title: p.name }));
  }, [options, prefixedForm]);

  return (
    <>
      <Block blended compact>
        <StepEditorAccordion title="Connection">
          <StepEditorAccordion.Row label="Data source">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={harvestSourceOptions}
              startAdornment={
                prefixedForm.get("datasourceId") ? (
                  <DataSourceIcon dataSource="harvest" size="small" />
                ) : null
              }
              value={prefixedForm.get("datasourceId")}
              onSelect={opt => prefixedForm.set("datasourceId")(opt.key)}
              placeholder="Select data source"
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
        </StepEditorAccordion>
      </Block>
      <Block blended compact>
        <StepEditorAccordion title="Time entry details">
          <StepEditorAccordion.Row label="External reference">
            <TagAwareInput
              variant="blended"
              size="small"
              value={prefixedForm.get("externalReferenceId")}
              onChange={prefixedForm.set("externalReferenceId")}
              placeholder="Required to identify the entry"
              tagSelectContext={{ workflowId }}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Project">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={projectOptions}
              value={prefixedForm.get("projectId")}
              onSelect={opt => prefixedForm.set("projectId")(opt.key)}
              placeholder="Select project"
              attachToRef={false}
              searchable
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Task">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={taskOptions}
              value={prefixedForm.get("taskId")}
              onSelect={opt => prefixedForm.set("taskId")(opt.key)}
              placeholder={prefixedForm.get("projectId") ? "Select task" : "Select project first"}
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="User">
            <TagSelector
              size="small"
              title="Select user"
              variant="blended"
              ctx={{ fieldTypes: ["email"], workflowId }}
              value={prefixedForm.get("harvestUser")}
              onSelect={tag => prefixedForm.set("harvestUser")(`{{${tag.tag}}}`)}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Date">
            <TagSelector
              size="small"
              title="Select date"
              variant="blended"
              value={prefixedForm.get("startDate")}
              ctx={{ fieldTypes: ["date"], workflowId }}
              onSelect={tag => prefixedForm.set("startDate")(`{{${tag.tag}}}`)}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Hours">
            <TagAwareInput
              size="small"
              variant="blended"
              value={prefixedForm.get("hours")}
              onChange={prefixedForm.set("hours")}
              tagSelectContext={{ workflowId }}
            />
          </StepEditorAccordion.Row>
        </StepEditorAccordion>
      </Block>
    </>
  );
};
